import { useTranslation } from "react-i18next";
import React, { useRef } from "react";
import { ArrowUpTrayIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { cn } from "../utils/style";

/**
 * Premium File Upload Component
 *
 * @param {string} id - HTML ID for the input and label
 * @param {string} name - Input name
 * @param {function} onChange - Change handler for file selection
 * @param {string} accept - Accepted file formats (e.g., ".csv,.xlsx")
 * @param {string} hint - Hint text for file formats/size
 * @param {string} fileName - Currently selected file name to display
 * @param {function} onClear - Optional handler to clear the selection
 * @param {string} className - Additional wrapper classes
 * @param {boolean} disabled - Disabled state
 */
function FileUpload({
  id = "file-upload",
  name = "file-upload",
  onChange,
  accept = ".csv,.xlsx",
  hint = "CSV, XLSX up to 10MB",
  fileName = null,
  onClear = null,
  className = "",
  disabled = false,
}) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const handleContainerClick = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  };
  return (
    <div className={cn("w-full max-w-2xl group", className)}>
      <div
        onClick={handleContainerClick}
        className={cn(
          "relative flex flex-col items-center justify-center rounded-2xl border-2 border-dashed transition-all duration-300 px-6 py-12 cursor-pointer overflow-hidden",
          fileName
            ? "border-emerald-200 bg-emerald-50/30"
            : "border-slate-200/60 bg-white/70 backdrop-blur-xl hover:border-primary-400 hover:bg-slate-50/50 hover:shadow-xl hover:shadow-primary-500/5",
          disabled && "opacity-50 cursor-not-allowed grayscale",
        )}
      >
        {/* Visual Accent */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

        <div className="relative text-center">
          {fileName ? (
            <div className="flex flex-col items-center animate-in fade-in zoom-in duration-300">
              <div className="h-16 w-16 bg-emerald-100 rounded-2xl flex items-center justify-center mb-4 shadow-sm">
                <DocumentTextIcon className="h-8 w-8 text-emerald-600" />
              </div>
              <h4 className="text-sm font-bold text-slate-900 mb-1 truncate max-w-md px-4">
                {fileName}
              </h4>
              <p className="text-[10px] font-black uppercase tracking-widest text-emerald-600/70">
                {t("Ready for processing")}
              </p>

              {onClear && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClear();
                  }}
                  className="mt-4 text-xs font-bold text-slate-400 hover:text-red-500 transition-colors uppercase tracking-widest"
                >
                  {t("Remove File")}
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="mx-auto h-16 w-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-6 border border-slate-100 group-hover:scale-110 transition-transform duration-300 group-hover:bg-primary-50">
                <ArrowUpTrayIcon
                  className="h-8 w-8 text-slate-300 group-hover:text-primary-500 transition-colors"
                  aria-hidden="true"
                />
              </div>

              <div className="flex flex-col items-center text-sm leading-6">
                <span className="relative rounded-lg px-4 py-2 font-black text-[11px] uppercase tracking-widest text-primary-600 bg-primary-50/50 border border-primary-100/50 shadow-sm transition-all group-hover:shadow-md group-hover:bg-primary-600 group-hover:text-white active:scale-[0.97]">
                  {t("Choose a file")}
                </span>
                <p className="mt-3 font-medium text-slate-500">
                  {t("or drag and drop here")}
                </p>
              </div>

              <p className="mt-4 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400/80">
                {hint}
              </p>
            </>
          )}
        </div>

        <input
          id={id}
          name={name}
          ref={inputRef}
          onChange={onChange}
          accept={accept}
          type="file"
          className="sr-only"
          disabled={disabled}
        />
      </div>
    </div>
  );
}
export default FileUpload;
